'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useCookies } from 'next-client-cookies';
import { Product } from 'shared/types/product/Product';
import { ProductQuery } from 'shared/types/query';
import { Facet } from 'shared/types/result/Facet';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import ProductList, { ProductListProps } from 'components/commercetools-ui/organisms/product/product-list';
import ProductListProvider from 'components/commercetools-ui/organisms/product/product-list/context';
import {
  buildFacetFilters,
  buildPaginatedQueries,
  deriveQuery,
} from 'components/commercetools-ui/organisms/product/product-list/helpers';
import {
  SortConfiguration,
  SubCategoriesDataProps,
  SubCategoryBannerProps,
  TextConfiguration,
} from 'components/commercetools-ui/organisms/product/product-list/types';
import { mapCategotry } from 'helpers/entity-mappers/map-category';
import { mapProduct } from 'helpers/entity-mappers/map-product';
import { flattenTree } from 'helpers/utils/flattenTree';
import { DefaultOverlay, usePageLoader } from 'providers/loader';
import { DataSource } from 'types/datasource';
import { Reference } from 'types/reference';
import { useCart, useProduct, useWishlist } from 'frontastic/hooks';
import { TasticProps } from 'frontastic/tastics/types';
import { TieredPriceData } from '../details';
import useFacetsConfiguration from './hooks/useFacetsConfiguration';

interface Props {
  textConfiguration?: TextConfiguration[];
  sortConfiguration?: SortConfiguration[];
  subCatConfig?: SubCategoryBannerProps[];
  viewFacets?: [];
  primaryLogoLight?: ImageProps;
  primaryLogoDark?: ImageProps;
  primaryLogoLink?: Reference;
  primaryLogoLinkMobile?: Reference;
  primaryLogoMobileDark?: ImageProps;
  primaryLogoMobileLight?: ImageProps;
  tagsToShow?: string;
  facetLoadMoreText?: string;
  facetSeeLessText?: string;
}

interface DataSourceProps {
  items: Product[];
  category: string;
  facets: Facet[];
  previousCursor?: string;
  nextCursor?: string;
  total: number;
  totalItems: number;
  subCategories?: SubCategoriesDataProps;
  textConfig?: Record<string, string>[];
  tieredPrice?: TieredPriceData[];
  calendarOptions?: any;
}

type ProductListWrappedProps = Pick<
  TasticProps<DataSource<DataSourceProps> & Props & ProductListProps>,
  'data' | 'categories'
>;

const ProductListWrapped = ({ data, categories }: ProductListWrappedProps) => {
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const textConfig: Record<string, string> = (data?.textConfiguration?.[0] ?? {}) as Record<string, string>;

  const config: {
    FrameHexColor: object[];
    variantHeadingLabel: object[];
  } = {
    FrameHexColor: (data.fontConfig ?? []).flatMap((item: any) => item.FrameHexColor),
    variantHeadingLabel: (data.fontConfig ?? []).flatMap((item: any) => item.variantHeadingLabel),
  };

  return (
    <ProductList
      products={(data.data?.dataSource?.items ?? []).map((product) => mapProduct(product, { locale }))}
      sortConfiguration={data?.sortConfiguration}
      textConfiguration={data?.textConfiguration}
      categories={categories}
      fontConfig={config}
      subCatConfig={data?.subCatConfig}
      subCategories={data?.data?.dataSource?.subCategories}
      textConfig={textConfig}
      tieredPrice={data?.tieredPrice as TieredPriceData[]}
      calendarOptions={textConfig?.calendarOptions}
      viewCategoryFacets={data?.viewCategoryFacets}
      viewDynamicFacets={data?.viewDynamicFacets}
      tagsToShow={data?.tagsToShow}
      facetLoadMoreText={data?.facetLoadMoreText}
      facetSeeLessText={data?.facetSeeLessText}
      primaryLogoLight={data?.primaryLogoLight}
      primaryLogoDark={data?.primaryLogoDark}
      primaryLogoLink={data.primaryLogoLink}
      primaryLogoLinkMobile={data?.primaryLogoLinkMobile}
      primaryLogoMobileDark={data?.primaryLogoMobileDark}
      primaryLogoMobileLight={data?.primaryLogoMobileLight}
      pageName={data?.name ?? ''}
      isDealsPage={data?.isDealsPage ?? false}
      pageHeading={data?.pageHeading ?? ''}
    />
  );
};

const filterMatchingVariants = (items: Product[]) => {
  return items.map((item) => ({
    ...item,
    variants: item.variants.filter((variant) => variant.isMatchingVariant !== false),
  }));
};

const ProductListTastic = ({
  data,
  categories,
}: TasticProps<
  DataSource<{ query: ProductQuery; subCategories: SubCategoriesDataProps }> & Props & ProductListProps
>) => {
  const { slug } = useParams();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { query } = useProduct();
  const { query: queryToCall } = data.data?.dataSource ?? { query: {} as ProductQuery };
  const [productData, setProductData] = useState<any>({});
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get('query') as string;
  const { hideLoader, showLoader, isLoading } = usePageLoader();
  const flattenedCategories = categories.flatMap((category) => flattenTree(category, 'descendants'));
  const buildFacetConfigFromViewFacets = [
    ...buildFacetFilters(data?.viewDynamicFacets?.[0]?.facetsGroup),
    ...buildFacetFilters(data?.viewCategoryFacets?.[0]?.facetsGroup),
  ];
  const { finalFacetsConfiguration: facetsConfiguration, isContainsP2RItems } = useFacetsConfiguration({
    facets: productData?.facets ?? [],
    facetsConfiguration: buildFacetConfigFromViewFacets,
    categories: flattenedCategories,
  });

  const { data: wishlist, addToWishlist, removeLineItem } = useWishlist();

  const isDealsPage = data?.isDealsPage ?? false;

  const { addItem, shippingMethods } = useCart();
  const isFetchingRef = useRef(false);
  const prevQueryKeyRef = useRef<string | null>(null);

  const finalQueryToCall = useMemo(() => {
    if (!isDealsPage) return queryToCall;

    return {
      ...queryToCall,
      productFilters: {
        filters: [
          {
            field: 'variants.prices.discounted',
            type: 'boolean',
          },
          ...(queryToCall?.productFilters?.filters ?? []),
        ],
        values: {
          ...(queryToCall?.productFilters?.values ?? {}),
          'variants.prices.discounted': true,
        },
      },
    };
  }, [isDealsPage, queryToCall]);

  useEffect(() => {
    const fetchProducts = async () => {
      showLoader();

      try {
        const derived = deriveQuery(finalQueryToCall, prevQueryKeyRef.current);

        // Case 1: same query except limit → pagination
        if (derived.isSameWithoutLimit && !derived.isSameWithLimit) {
          await handlePagination();
          return;
        }

        // Case 2: completely new query
        if (!derived.isSameWithLimit) {
          prevQueryKeyRef.current = derived.fullQuery;
          await handleNewQuery(derived.queryWithoutLimit);
          return;
        }

        // Case 3: exactly same query → do nothing (or pagination if needed)
      } catch (err) {
        console.error(err);
        resetProducts();
      } finally {
        isFetchingRef.current = false;
        hideLoader();
      }
    };

    void fetchProducts();
  }, [queryToCall]);

  const handleNewQuery = async (queryKey: string) => {
    prevQueryKeyRef.current = queryKey;

    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    const res = await query({ ...finalQueryToCall });

    if (res.isError) {
      resetProducts();
      return;
    }

    setProductData(res.data);
  };

  const handlePagination = async () => {
    const desiredLimit = Number(queryToCall?.limit ?? 40);
    const alreadyFetched = productData?.items?.length ?? 0;

    if (!shouldFetchMore(desiredLimit, alreadyFetched)) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    const paginatedQueries = buildQueries(desiredLimit, alreadyFetched);
    const responses = await Promise.all(paginatedQueries.map((q) => query({ ...q })));

    if (responses.some((r) => r.isError)) {
      resetProducts();
      return;
    }

    mergeResponses(responses);
  };

  const shouldFetchMore = useCallback((limit: number, fetched: number) => limit > fetched, []);

  const buildQueries = (limit: number, fetched: number) =>
    finalQueryToCall?.limit ? buildPaginatedQueries(limit, fetched, finalQueryToCall) : [finalQueryToCall];
  const mergeResponses = (responses: any[]) => {
    const base = responses[0].data;
    const newItems = responses.flatMap((res) => res?.data?.items ?? []);

    setProductData((prev: any) => ({
      ...base, // facets, total, etc.
      items: [...(prev.items || []), ...newItems],
      total: base?.total ?? 0,
    }));
  };

  const resetProducts = () => {
    setProductData({
      total: 0,
    });
  };

  // Apply the filter to the items
  const filteredItems = useMemo(() => filterMatchingVariants(productData?.items ?? []), [productData]);

  // Update the data object with filtered items
  const updatedData = useMemo(() => {
    return {
      ...data,
      data: {
        ...data.data,
        dataSource: {
          ...productData,
          subCategories: data?.data?.dataSource?.subCategories ?? {},
          items: filteredItems,
        },
      },
    };
  }, [data, productData, filteredItems]);

  return productData?.total === undefined ? (
    !isLoading && (
      <div className="pointer-events-auto absolute inset-0 z-[999999] opacity-100 transition-opacity duration-150">
        <DefaultOverlay />
      </div>
    )
  ) : (
    <ProductListProvider
      uiState={{
        searchQuery,
        slug: slug?.at(-1),
        previousCursor: productData.previousCursor,
        nextCursor: productData.nextCursor,
        totalItems: productData.total,
      }}
      categories={flattenedCategories.map((category) => mapCategotry(category, { locale }))}
      shippingMethods={shippingMethods.data}
      facetsConfiguration={facetsConfiguration}
      wishlist={wishlist}
      addToWishlist={async (lineItem, count) => {
        if (wishlist) await addToWishlist(wishlist, lineItem, count);
      }}
      removeFromWishlist={async (lineItem) => {
        if (wishlist) await removeLineItem(wishlist, lineItem);
      }}
      onAddToCart={addItem}
      isContainsP2RItems={isContainsP2RItems}
    >
      <ProductListWrapped data={updatedData} categories={categories} />
    </ProductListProvider>
  );
};
export default ProductListTastic;
